import React, {Fragment, useState} from 'react';
import ReactDOM from 'react-dom';
import * as THREE from 'three';
import {Canvas, extend} from '@react-three/fiber';
import {OrbitControls} from "three/examples/jsm/controls/OrbitControls";
import {ErrorBoundary} from 'react-error-boundary';
import clone from 'just-clone';
import semver from 'semver';

import {Computation} from './computation';
import {Elements} from './element';
import {ElementsTraj} from './elementtraj';
import {Nav, PlaneNav, ElementNav, NodeNav, AnalysisNav, TopNav, TrajectoryNav} from './navigation';
import {Planes} from './plane';
import {Nodes} from './node';
import {modelLibrary} from './modelLoader';
import {CameraControls} from './cameracontrols';
import {Settings} from './settings';
// import {CustomElement} from './customelement';

import useLocalStorageState from 'use-local-storage-state';
import create from 'zustand'

import {Container, Row, Col} from 'react-bootstrap';

import 'bootstrap/dist/css/bootstrap.min.css';
import './index.css';
import {version} from './version';

import {GoogleOAuthProvider, GoogleLogin} from '@react-oauth/google';
import {jwtDecode} from 'jwt-decode';
import {trackLogin} from './login';
// import {LoginButton} from './loginbutton';

extend({ OrbitControls });

//a faster store for stuff during animation
const useStore = create( set => ({
                                  lstep: 0, 
                                  show: false,
                                  toggle: () => set( state => ({show: !state.show}) ),
                                  explode: 100,
                                  increaseExplode: () => set( state =>({explode: state.explode+50})),
                                  decreaseExplode: () => set( state =>({explode: state.explode-50})),
                                  setExplode: (val) => set( state =>({explode: val})),
                                  setLoadstep: (val) => set( state =>({lstep: val})),
                                }))
export const useAnimStore = useStore;

function Scene(props) {

    // const [nodes,setNodes] = useState(
    const [nodes,setNodes] = useLocalStorageState('nodes',
      [
        {
          id: 1,
          coord: [0,0,500],
          fix: true,
        },
        {
          id: 2,
          coord: [0,0,0],
          fix: false,
        },
        {
          id: 3,
          coord: [500,0,0],
          fix: false,
        },
        {
          id: 4,
          coord: [500,0,500],
          fix: true,
        },
      ]
    );

    // const [elements, setElements] = useState(
    const [elements, setElements] = useLocalStorageState('elements',    
      [
        {
          id: 1,
          connect: [1,2],
          locY: [0,1,0],
          type: 'leaf',
        },
        {
          id: 2,
          connect: [2,3],
          locY: [0,1,0],
          type: 'rigid',
        },
        {
          id: 3,
          connect: [3,4],
          locY: [0,1,0],
          type: 'leaf',
        },
        // {
        //   id: 4,
        //   connect: [2,3],
        //   locY: [0,1,0],
        //   type: 'custom',
        //   file: './inner.STL',
        //   color: 'red',
        //   scale: 5,
        //   // rot: new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1,0,0),Math.PI/2),
        //   rot: [0.707107,0,0,0.707107],
        // }
      ]
    );  

    // const [planes, setPlanes] = useState(
    const [planes, setPlanes] = useLocalStorageState('planes',    
      [
        {
        id: 1,
        dir: [0,1,0],
        center: [0,0,0],
        orien: [0,0,0,1],
        show: true,
        type: 'cart',
        },
      ]
    );

    const visSettings = 
      { 
        timeSteps: 11,
        lineSteps: 8,
        crossSteps: 1,
        wSteps: 2,
        tSteps: 2,
        rate: 2,
      };

    const [mode, setMode] = useState('elements');
    const [activePlane, setActivePlane] = useState(true);
    const [activePlaneId, setActivePlaneId] = useState(1);
    const [activeNode, setActiveNode] = useState(false);
    const [activeNodeId, setActiveNodeId] = useState(null);
    const [activeElement, setActiveElement] = useState(false);
    const [activeElementId, setActiveElementId] = useState(null);
    const [activeOCId, setActiveOCId] = useState(0);
    const [activeUCId, setActiveUCId] = useState(0);
    const [orbitControlsEnabled, setOrbitControlsEnabled] = useState(true);
    const [hasAnalyzed, setHasAnalyzed] = useState(false);
    const [stressMode, setStressMode] = useState('moments');
    const [analysisType, setAnalysisType] = useState('overconstraints');
    const [doLUQ, setDoLUQ] = useState(true);
    const [analysisRes, setAnalysisRes] = useState(null);
    const [transformGridType,setTransformGridType] = useState('translate');

    const [showState,setShowState] = useState(false);
    const [showOrigin,setShowOrigin] = useState(true);
    const [showNav,setShowNav] = useState(true);
    const [rigidSize,setRigidSize] = useState(120);

    const stateList = [{
      'planes': planes, 'nodes': nodes, 'elements': elements, 
      'mode': mode, 
      'activePlane': activePlane, 'activePlaneId': activePlaneId, 
      'activeNode': activeNode, 'activeNodeId': activeNodeId, 
      'activeElement': activeElement, 'activeElementId': activeElementId, 
      'activeOCId': activeOCId, 'activeUCId': activeUCId, 
      'orbitControlsEnabled': orbitControlsEnabled, 'hasAnalyzed': hasAnalyzed,
      'stressMode': stressMode, 'analysisType': analysisType, 'doLUQ': doLUQ, 'transformGridType': transformGridType,
      'showState': showState, 'showOrigin': showOrigin, 'showNav': showNav,
      'rigidSize': rigidSize,
      'version': version,
      }
    ];

    //this is stored in the file when downloading model
    const stateList2 = [
      {
        'planes': planes,
        'nodes': nodes.map( (n) => ({"id": n.id, "coord": n.coord, "fix": n.fix}) ),
        'elements': elements.map( (e) => ({"id": e.id, "connect": e.connect, "type": e.type, "locY": e.locY }) ),
        'version': version,
      }
    ];

  const setNewState = (nodes,elements,planes) => {
    setNodes(nodes);
    setElements(elements);
    setPlanes(planes);

    //other defaults, not stored in library
    setMode('elements');
    setActivePlane(true);
    setActivePlaneId(1);
    setActiveNode(false);
    setActiveNodeId(null);
    setActiveElement(false);
    setActiveElementId(null);
    setActiveOCId(0);
    setActiveUCId(0);
    setOrbitControlsEnabled(true);
    setHasAnalyzed(false);
    setStressMode('moments');
    setAnalysisType('overconstraints');
    setDoLUQ(true);
    setAnalysisRes(null);
    setTransformGridType('translate');
  }

  const handleLoadModel = (idDesired) => {

      let ii = modelLibrary.findIndex( m => m.id === idDesired);
      // const state = {...modelLibrary[ii].state}; //this creates a shallow copy and remembers changes to previous models
      
      const state = clone(modelLibrary[ii].state); //library function to create a deep copy
      setNewState(state.nodes,state.elements,state.planes);

  }

  const handleLoadFile = (e) => {
    if(e.target.files.length > 0){
      const fileReader = new FileReader();
      fileReader.readAsText(e.target.files[0], "UTF-8");
      fileReader.onload = (e) => {
        const data = e.target.result;
        let ns = JSON.parse(data);
        ns = ns[0];
        
        if(ns.hasOwnProperty('version')){
          if(semver.neq(ns.version,version)){
            console.log('Error loading file. File was created with '+ns.version);
            alert('Error loading file. File was created with '+ns.version);
            return;
          }
          //then, ok and proceed.
        } 
        else {
          console.log('Error loading file. File was created with v1.6.0 or earlier');
          alert('Error loading file. File was created with v1.6.0 or earlier');
          return;
        }

        //we need to batch these state updates, since these updates are
        //called from an async function -- normally, they wouldn't automatically
        //be batched, leading to issues where the app tries to render
        //with half-updated state info (e.g. new nodes with old elements)
        ReactDOM.unstable_batchedUpdates(() => {
          // setNodes(ns.nodes);
          // setElements(ns.elements);
          // setPlanes(ns.planes);
          setNewState(ns.nodes,ns.elements,ns.planes);
        })
      }
    }
  }

  const handleModeChange = (val) => {
    if (val !== mode){
      setMode(val);
      setActiveNode(false);
      setActiveElement(false);
      setActiveOCId(0);
      setActiveUCId(0);
      setHasAnalyzed(false);
    }
  };

  const handleSaveModel = () => {
    const link = document.createElement("a");
    const file = new Blob([JSON.stringify(stateList2,null,4)], {type: 'text/plain'});
    link.href = URL.createObjectURL(file);
    link.download = "model.txt";
    document.body.appendChild(link); //required for this to work in firefox
    link.click();
    document.body.removeChild(link);
  }

  const handleAddPlane = () => {

    const idMax = Math.max(...planes.map(p=>p.id));
    setPlanes(
      [
        ...planes,
        {
          id: idMax+1,
          // dir: [0,1,0],
          orien: [0,0,0,1],
          center: [0,0,0],
          show: true,
          type: "cart",
        }
      ]
    );
    setActivePlaneId(idMax+1);
    setActivePlane(true);

  }

  const handleAddNode = (val) => {
    
    // test to see if clicked node already exists
    // from: https://stackoverflow.com/questions/41661287/how-to-check-if-an-array-contains-another-array
    // if (!this.arrayExistsInArray(val,this.state.nodes)){
    //   this.setState({nodes: [...this.state.nodes, val]})
    // }

    const found = nodes.some(
      n => ( n.coord[0]===val[0] && n.coord[1]===val[1] && n.coord[2]===val[2] )
      );

    if (!found) {
      // add to node list
      // find largest occuring index
      const n_id_max = Math.max(...nodes.map(n => n.id), 0);

      setNodes(
        [
          ...nodes,
          {
            id: n_id_max+1,
            coord: val,
            fix: false,
          }
        ]
      )
    };
  };

  const addElement = (p,q) => {

      const found = elements.some( 
        el => ( (el.connect[0]===p && el.connect[1]===q) || (el.connect[0]===q && el.connect[1]===p) )
                                            );
      
      if (!found){

          let p_id = nodes.findIndex( n => n.id === p);
          let q_id = nodes.findIndex( n => n.id === q);
          let locX = new THREE.Vector3();
          let p_coord = new THREE.Vector3();
          let q_coord = new THREE.Vector3();
          p_coord.fromArray(nodes[p_id].coord);
          q_coord.fromArray(nodes[q_id].coord);

          locX.subVectors(q_coord,p_coord);
          // const length = locX.length();
          locX.normalize()
          
          //find suitable locY
          const locY1 = new THREE.Vector3(0,1,0);
          const locY2 = new THREE.Vector3(1,0,0);
          const locY = new THREE.Vector3();

          //try locY1
          if(Math.abs(locX.dot(locY1))<0.01) {
            locY.copy(locY1)
          }
          //try locY2
          else if(Math.abs(locX.dot(locY2))<0.01){
            locY.copy(locY2)
            }
          //construct some vector perpendicular to locX,
          //based on https://math.stackexchange.com/questions/137362/how-to-find-perpendicular-vector-to-another-vector
          else {
            let tmp= [
              (locX.x ** 2)+(locX.y ** 2),
              (locX.x ** 2)+(locX.z ** 2),
              (locX.y ** 2)+(locX.z ** 2)
            ]
            let locYcandid = [
              [-locX.y,locX.x,0],
              [-locX.z,0,locX.x],
              [0,locX.z,-locX.y]
            ]
            const ii = tmp.indexOf(Math.max(...tmp))
            locY.fromArray(locYcandid[ii]).normalize();
          }

          // add to element list
          // find largest occuring index
          const e_id_max = Math.max(...elements.map(e => e.id), 0);

          setElements(
            [
             ...elements,
             {
              id: e_id_max+1,
              connect: [p,q],
              locY: [locY.x, locY.y, locY.z],
              type: 'wire',
             }        
            ]
          )

      }

  };

  const handleSelectNode = (nodeid) => {
    // if in nodes mode, make active and show props
    if (mode === 'nodes'){
      if (!activeNode) {
          setActiveNode(true);
      }
      else {
          // there's already an active node
          if (activeNodeId === nodeid){
            // same node, so deselect
            setActiveNode(false);
          }
          else {
            // different node
            setActiveNode(true);
          }
      }
    }

    // if in elements mode, use for element construction
    if (mode === 'elements'){
      setActiveElement(false); //disable any element that may have been active
      if (!activeNode) {
          // first node that's been clicked, so set active flag (anticipating second click)
          setActiveNode(true);
      }
      else {
          // one node has already been selected
          if (activeNodeId === nodeid){
            // same node, so deselect
            setActiveNode(false);
          }
          else {
            // different node: so, create element:
            addElement(activeNodeId, nodeid);
            setActiveNode(false);
          }
      }
    }

    // after checking the state activeNodeId (in the above), update it
    setActiveNodeId(nodeid);

  };

  const handleDeleteNode = (id) => {
    
    //check if node is attached to elements
    const attached = elements.some(e => (e.connect[0] === id || e.connect[1] === id));
    
    if (!attached){
      const index = nodes.findIndex(n => n.id === id);

      if (index > -1){
        let nodesNew = [...nodes];
        nodesNew.splice(index,1);
        setNodes(nodesNew);
      }
      if (activeNodeId === id){
        setActiveNode(false);
        setActiveNodeId(null);
      } 
    }
    else {

    }
  }
  
  const handleSelectPlane = (id) => {
    if (!activePlane) {
      setActivePlane(true);
      setActivePlaneId(id);
      handleTogglePlaneVisibility(id,true);
    }
    else {
      if (activePlaneId === id){
        //deactivate if active
        setActivePlane(false);
        setActivePlaneId(null);
      }
      else {
        setActivePlane(true);
        setActivePlaneId(id);
        handleTogglePlaneVisibility(id,true);
      }
    }
  };

  const handleTogglePlaneVisibility = (id,val) => {
    const index = planes.findIndex(p => p.id === id);
    let newPlanes = [...planes];
    newPlanes[index].show = val;
    setPlanes(newPlanes);
    if (!activePlane && activePlaneId === id){
      setActivePlane(true);
      setActivePlaneId(id);
    }

  }

  const handleSelectElement = (id) => {
    setActiveNode(false); //disable any node that may have been active
    if (!activeElement) {
      setActiveElement(true);
      setActiveElementId(id);
    }
    else {
      if (activeElementId === id){
        setActiveElement(false);
      }
      else {
        setActiveElement(true);
        setActiveElementId(id);
      }
    }
  };

  const handlePointerMissed = () => {
    if(mode === 'elements'){
      setActiveElement(false); //element could be active in element mode
      setActiveNode(false); //node could be active in element mode
    }
    if(mode === 'nodes'){
      setActiveNode(false);
    }
  }

  const handleDeletePlane = (id) => {
    
    //don't delete first plane
    if (id === 1){
      //just reset values
      let planesNew = [...planes];
      // planesNew[0].dir = [0,1,0];
      planesNew[0].orien = [0,0,0,1];
      planesNew[0].center = [0,0,0];
      planesNew[0].show = true;
      planesNew[0].type = 'cart';
      setPlanes(planesNew);

    }
    else {

      const index = planes.findIndex(p => p.id === id);

      if (index > 0){
        let planesNew = [...planes];
        planesNew.splice(index,1);
        setPlanes(planesNew);
      }
      if (activePlaneId === id){
        setActivePlane(false);
        setActivePlaneId(null);
      }
    }
  }

  const handleDeleteElement = (id) => {
    const index = elements.findIndex(e => e.id === id);

    if (index > -1){
      let elementsNew = [...elements];
      elementsNew.splice(index,1);
      setElements(elementsNew);
    }
    if (activeElementId === id){
      setActiveElement(false);
      setActiveElementId(null);
    }
  }

  const handleDelete = () => {
    if (mode === 'elements' && activeElement){
      //delete active element
      const index = elements.findIndex(e => e.id === activeElementId);

      if (index > -1){
        let elementsNew = [...elements];
        elementsNew.splice(index,1);
        setElements(elementsNew);
        setActiveElement(false);
        setActiveElementId(null);
      }
    }
  }

  const handleOrbitControls = (val) => {
        setOrbitControlsEnabled(val);
  }

  const handleUpdateGrid = (id,pos,orien) => {
    const newPlanes = [...planes];
    
    const i = planes.findIndex(p => p.id === id);

    newPlanes[i].center = [pos.x,pos.y,pos.z];
    // newPlanes[i].dir = [dir.x,dir.y,dir.z];
    
    //important: assumes that orien passed in is a Quaternion() object
    newPlanes[i].orien = orien.toArray();
    setPlanes(newPlanes);
    // console.log(newPlanes)
  }

  const handleUpdateLocY = (dir,id) => {
      const el_ii = elements.findIndex( e => e.id === id);
      const newElements = [...elements];
      
      //first, do some checks??!

      newElements[el_ii].locY = [dir.x, dir.y, dir.z];
      setElements(newElements);
  }

  const handleSelectOC = (e) => {
    const selectedOC = parseInt(e.target.value);
    // console.log('hiero');
    // console.log(selectedOC);
    if(Number.isInteger(selectedOC) && selectedOC>0 && selectedOC <= analysisRes.nover){
      console.log('ok')
      setActiveOCId(selectedOC-1);
      setHasAnalyzed(false);
    }
    else{console.log('nok')}
  };

  const handleSelectUC = (e) => {
    const selectedUC = parseInt(e.target.value);
    if(Number.isInteger(selectedUC) && selectedUC>0 && selectedUC <= analysisRes.nunder){
      setActiveUCId(selectedUC-1);
      setHasAnalyzed(false);
    }
  };

 const handleChangeDir = (e,id) => {
  //called if user enters - numerically - new desired normal vector for a plane

  let dirNew = e.target.value;
  dirNew = dirNew.split(',').map(Number); //somehow Number gets rid of whitespace and converts appropriately to float or int

  const NaNFound = dirNew.some(n => Number.isNaN(n));

  if (dirNew.length === 3 && !NaNFound){

    const dirTest = new THREE.Vector3();
    dirTest.fromArray(dirNew);
    
    if (dirTest.length() !== 0){

      dirTest.normalize();

      console.log(dirTest)

      let gridOrientation = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0,1,0), dirTest);
      
      const p_ii = planes.findIndex( p => p.id === id);
      const newPlanes = [...planes];
      // newPlanes[p_ii].dir = [dirTest.x, dirTest.y, dirTest.z];
      newPlanes[p_ii].orien = gridOrientation.toArray(); //turn it into array before storing in state;
      console.log('store')
      setPlanes(newPlanes);

    }

  }
}

 const handleChangeLocY = (e,id) => {
  let locYNew = e.target.value;
  locYNew = locYNew.split(',').map(Number); //somehow Number gets rid of whitespace and converts appropriately to float or int
  console.log(locYNew)

  const NaNFound = locYNew.some(n => Number.isNaN(n));
  // console.log(NaNFound)

  if (locYNew.length === 3 && !NaNFound){

    const locYTest = new THREE.Vector3();
    locYTest.fromArray(locYNew);
    
    if (locYTest.length() !== 0){

      locYTest.normalize();

      //lot of effort to find the corresponding locx:
      const el_ii = elements.findIndex( e => e.id === id);
      const p = elements[el_ii].connect[0];
      const q = elements[el_ii].connect[1];

      let p_ii = nodes.findIndex( n => n.id === p);
      let q_ii = nodes.findIndex( n => n.id === q);
      let locX = new THREE.Vector3();
      let p_coord = new THREE.Vector3();
      let q_coord = new THREE.Vector3();
      p_coord.fromArray(nodes[p_ii].coord);
      q_coord.fromArray(nodes[q_ii].coord);

      locX.subVectors(q_coord,p_coord);
      // const length = locX.length();
      locX.normalize();

      
      if (Math.abs(locX.dot(locYTest))<0.01){
        const newElements = [...elements];
        newElements[el_ii].locY = [locYTest.x, locYTest.y, locYTest.z];
        console.log('store')
        setElements(newElements);
      }
      else{
        console.log('not ok')
        console.log(locX)
        console.log(locYTest)
      }

    }
    }
  }

  const handleChangeElementType = (e,id,val) => {
    //find index corresponding to element id:
    const ii = elements.findIndex( el => el.id === id);

    const newElements = [...elements];
    if(val === 'rigid'){ newElements[ii].type = 'rigid'; }
    if(val === 'leaf'){ newElements[ii].type = 'leaf'; }
    if(val === 'wire'){ newElements[ii].type = 'wire'; }
    if(val === 'notch'){ newElements[ii].type = 'notch'; }

    setElements(newElements);

    if (e !== ''){
      //stupid thing: necessary because browser may add focus on buttons after clicking,
      // preventing the use of the hotkeys we're setting below
      e.target.blur() 
    }
  }

  const handleChangePlaneType = (e,id,val) => {
    //find index corresponding to planes id:
    const ii = planes.findIndex( p => p.id === id);

    const newPlanes = [...planes];
    if(val === 'cart'){ newPlanes[ii].type = 'cart'; }
    if(val === 'polar'){ newPlanes[ii].type = 'polar'; }
    
    setPlanes(newPlanes);

    if (e !== ''){
      //stupid thing: necessary because browser may add focus on buttons after clicking,
      // preventing the use of the hotkeys we're setting below
      e.target.blur() 
    }
  }

  const handleChangeFix = (e,id,val) => {
      //find index corresponding to node id:
      const ii = nodes.findIndex( no => no.id === id);

      const newNodes = [...nodes];
      if(val === 'free'){ newNodes[ii].fix = false; }
      if(val === 'fixed'){ newNodes[ii].fix = true; }

      setNodes(newNodes);

      if (e !== ''){
        //stupid thing: necessary because browser may add focus on buttons after clicking,
        // preventing the use of the hotkeys we're setting below
        e.target.blur() 
      }
    }

  const handleChangeStressMode = (e,val) => {
      setHasAnalyzed(false);
      setStressMode(val);

      if (e !== ''){
        //stupid thing: necessary because browser may add focus on buttons after clicking,
        // preventing the use of the hotkeys we're setting below
        e.target.blur() 
      }
    }

  const handleChangeAnalysisType = (e,val) => {
    setAnalysisType(val);
    if (e !== ''){
        //stupid thing: necessary because browser may add focus on buttons after clicking,
        // preventing the use of the hotkeys we're setting below
        e.target.blur() 
    }
  }

  const handleChangeDoLUQ = (e,val) => {
    setHasAnalyzed(false);
    setDoLUQ(val);
    if (e !== ''){
        //stupid thing: necessary because browser may add focus on buttons after clicking,
        // preventing the use of the hotkeys we're setting below
        e.target.blur() 
    }
  }

  const handleChangeTransformGridType = (e,val) => {
    setTransformGridType(val);
    if (e !== ''){
        //stupid thing: necessary because browser may add focus on buttons after clicking,
        // preventing the use of the hotkeys we're setting below
        e.target.blur() 
    }
  }

  const handleSetRigidSize = (e) => {
    const inputVal = parseInt(e.target.value);
    if(Number.isInteger(inputVal) && inputVal>10 && inputVal <= 200){
      setRigidSize(inputVal);
    }
  };

  const handleStoreComputation = (res,newElements,newNodes) => {
    
    // the following state updates all need to be batched
    // this was causing the long-standing bug, where
    // when an underconstraint had just been viewed (analysis mode underconstraint, nunder>0)
    // and the system was modified to have zero underconstraints (e.g. by tilting one of the beams in the 'three parallel leafs')
    // upon going back to analysis, there would be the error that 
    // line 29 of deformingelement.js: let dqp = [...props.dcoord.dqp]; was not iterable.
    // Reason is that after setting setHasAnalyzed(true);, it would already trigger a render of the deformation visualization,
    // without the complete new state being available (i.e. nunder=0, so no visualization is possible)
    // One could put setHasAnalyzed(true); last in the call, but that seemed to cause other issues of multiple consecutive 
    // computations happening.
    // Perhaps this is fixed in newer versions of React that do more batch updating by default.

    ReactDOM.unstable_batchedUpdates(() => {

      setHasAnalyzed(true); //call this to avoid infinite loops. Only call after all datastructures have been filled
      setAnalysisRes(res);
      if (newElements.length > 0 && newNodes.length > 0){
        console.log('stored1')
        setElements(newElements);
        setNodes(newNodes);
      }
      console.log('stored2')

    });
  }

  return (
  <ErrorBoundary
      fallbackRender={
        ({error,resetErrorBoundary}) => {
          return (
            <div role="alert">
              <p>An error occured:</p>
              <pre>{JSON.stringify(error,Object.getOwnPropertyNames(error),4)}</pre>
              <p>Current state of the program:</p>
              <pre>{JSON.stringify(stateList,null,4)}</pre>
            </div>
          )
      }
      }
      >
    <Container className="vh-100" fluid>
      <Row className={"bg-light"}>
        <TopNav
          version={version}
          handleLoadModel={handleLoadModel}
          handleLoadFile={handleLoadFile}
          handleSaveModel={handleSaveModel}
          showState={showState}
          showOrigin={showOrigin}
          setShowState={setShowState}
          setShowOrigin={setShowOrigin}
          showNav={showNav}
          setShowNav={setShowNav}
          setSignedIn={props.setSignedIn}
        />
      </Row>
      { showState && <Settings rigidSize={rigidSize} setRigidSize={handleSetRigidSize} /> }
      <Row className={"h-75"}>
        <Col lg={showNav ? 7 : undefined} xl={showNav ? 6 : undefined} className={"canvascol"}>
            <Canvas 
                  camera={{
                    position: [840,960,1560],
                    fov: 35,
                    // aspect: window.innerWidth/window.innerHeight,
                    aspect: 1,
                    near: 1,
                    far: 10000,
                    //note: camera lookat is set using useThree in the CameraControls component
                  }}
                  dpr={window.devicePixelRatio}
                  onCreated={
                    ({raycaster}) =>{
                      // console.log(raycaster);
                      raycaster.params.Line.threshold = 10; //threshold on general Line, but only line currently is the plane gridhelper
                    }
                  } // for access to the raycaster
                  onPointerMissed={handlePointerMissed}
                  frameloop="demand"
                  >
                      <ambientLight color={0x606060}/>
                      <pointLight position={[10,10,10]} />
                      <directionalLight color={0xffffff} position={[1,0.75,0.5]} />
                      {orbitControlsEnabled && <CameraControls />}
                      <Planes
                        planes={planes}
                        activePlane={activePlane}
                        activePlaneId={activePlaneId}
                        mode={mode}
                        transformGridType={transformGridType}
                        addNode={handleAddNode}
                        enableOrbitControls={handleOrbitControls}
                        updateGrid={handleUpdateGrid}
                      />
                      { !((mode === 'analyze' && analysisType === 'underconstraints') || (mode === 'trajectory'))  &&
                      <Nodes 
                        nodes={nodes}
                        mode={mode}
                        selectNode={handleSelectNode}
                        activeNode={activeNode}
                        activeNodeId={activeNodeId}
                      />
                      }
                      { mode === 'trajectory' && elements[0].traj !== undefined &&
                      <ElementsTraj
                        elements={elements}
                        nodes={nodes}
                        mode={mode}
                        activeElement={activeElement}
                        activeElementId={activeElementId}
                        hasAnalyzed={hasAnalyzed}
                        analysisType={analysisType}
                        selectElement={handleSelectElement}
                        enableOrbitControls={handleOrbitControls}
                        updateLocY={handleUpdateLocY}
                        analysisRes={analysisRes}
                        visSettings={visSettings}
                      />
                      }
                      { mode !== 'trajectory' &&
                      <Elements
                        elements={elements}
                        nodes={nodes}
                        mode={mode}
                        activeElement={activeElement}
                        activeElementId={activeElementId}
                        hasAnalyzed={hasAnalyzed}
                        analysisType={analysisType}
                        selectElement={handleSelectElement}
                        enableOrbitControls={handleOrbitControls}
                        updateLocY={handleUpdateLocY}
                        analysisRes={analysisRes}
                        visSettings={visSettings}
                        rigidSize={rigidSize}
                      />
                      }
                      {
                      mode === 'analyze' && !hasAnalyzed &&
                      <Computation 
                        nodes={nodes} 
                        elements={elements}
                        activeOCId={activeOCId}
                        activeUCId={activeUCId}
                        storeComputation={handleStoreComputation}
                        setHasAnalyzed={setHasAnalyzed}
                        setElements={setElements}
                        setNodes={setNodes}
                        stressMode={stressMode}
                        doLUQ={doLUQ}
                         />
                        }
                        }
                      }
                      {
                        mode !== 'analyze' && mode !== 'trajectory' && showOrigin &&
                        <Fragment>
                        <arrowHelper args={[new THREE.Vector3(1,0,0),new THREE.Vector3(0,0,0),150,0xff0000]} />
                        <arrowHelper args={[new THREE.Vector3(0,1,0),new THREE.Vector3(0,0,0),150,0x00ff00]} />
                        <arrowHelper args={[new THREE.Vector3(0,0,1),new THREE.Vector3(0,0,0),150,0x0000ff]} />
                        </Fragment>
                      }
            </Canvas>
        </Col>
        <Col lg={5} xl={6} className={'navcol'} style={{display: showNav ? 'block' : 'none'}}>
          <Row>&nbsp;</Row>
          <Row>&nbsp;</Row>
          <Row className="text-center">
            <Nav mode={mode} onModeChange={handleModeChange} onDelete={handleDelete} elements={elements} />
          </Row>
          <Row>&nbsp;</Row>
          <Row>&nbsp;</Row>
          {
            mode === 'planes' &&
            <PlaneNav
              planes={planes}
              activePlane={activePlane}
              activePlaneId={activePlaneId}
              selectPlane={handleSelectPlane}
              addPlane={handleAddPlane}
              deletePlane={handleDeletePlane}
              changeDir={handleChangeDir}
              transformGridType={transformGridType}
              changeTransformGridType={handleChangeTransformGridType}
              togglePlaneVisibility={handleTogglePlaneVisibility}
              changePlaneType={handleChangePlaneType}
            />
          }
          {
            mode === 'nodes' &&
            <NodeNav
              nodes={nodes}
              elements={elements}
              activeNode={activeNode}
              activeNodeId={activeNodeId}
              selectNode={handleSelectNode}
              changeFix={handleChangeFix}
              deleteNode={handleDeleteNode}
            />
          }
          {
            mode === 'elements' &&
            <ElementNav 
              elements={elements} 
              activeElement={activeElement} 
              activeElementId={activeElementId} 
              selectElement={handleSelectElement} 
              changeLocY={handleChangeLocY} 
              changeElementType={handleChangeElementType}
              deleteElement={handleDeleteElement}
            />
          }
          {
            mode === 'analyze' &&
            <AnalysisNav
              activeOCId={activeOCId}
              activeUCId={activeUCId}
              selectOC={handleSelectOC}
              selectUC={handleSelectUC}
              analysisRes={analysisRes}
              hasAnalyzed={hasAnalyzed}
              stressMode={stressMode}
              analysisType={analysisType}
              changeStressMode={handleChangeStressMode}
              changeAnalysisType={handleChangeAnalysisType}
              doLUQ={doLUQ}
              changeDoLUQ={handleChangeDoLUQ}
            />
          }
          {
            mode === 'trajectory' && elements[0].traj !== undefined &&
            <TrajectoryNav elements={elements} />
          }
        </Col>
      </Row>
      {
        showState &&
        <Row>
        <pre>{JSON.stringify(stateList2,null,4)}</pre>
        <pre>{JSON.stringify(stateList,null,4)}</pre>
        </Row>
      }
    </Container>
    </ErrorBoundary>
  );
}

// const Bomb = () => {
//   throw new Error('boem');
// }

function App() {

  const [signedIn, setSignedIn] = useLocalStorageState('signedIn',false);
  const [lastVisit, setLastVisit] = useLocalStorageState('lastVisit',0);

  const responseMessage = (response) => {
        // console.log('success')
        // console.log(response);
        const decoded = jwtDecode(response.credential);
        // console.log('Email:', decoded.email);
        trackLogin(decoded.email);
        setLastVisit(new Date().getTime());
        setSignedIn(true);
    };

  const errorMessage = (error) => {
      console.log('Error Google request');
      // console.log(error);
  };

  const getTimeSinceLastVisit = () => {
      const now = new Date().getTime();
      const timePassed = now - lastVisit;
      const secondsPassed = Math.floor(timePassed / 1000);
      const minutesPassed = Math.floor(secondsPassed / 60);
      const hoursPassed = Math.floor(minutesPassed / 60);
      const daysPassed = Math.floor(hoursPassed / 24);

      return daysPassed;
  };

  console.log(new Date().getTime())
  console.log(getTimeSinceLastVisit())

  if(getTimeSinceLastVisit() > 3 && signedIn){
      setSignedIn(false);
  }

  return (
    <Fragment>
    { 
      !signedIn && <GoogleLogin onSuccess={responseMessage} onError={errorMessage} /> 
    }
    { 
      signedIn && 
      <Fragment>
        <Scene setSignedIn={setSignedIn} />
      </Fragment>
    }
    </Fragment>
  )

  // return (
  //   <Scene setSignedIn={setSignedIn} />
  // )
}


ReactDOM.render(
  <GoogleOAuthProvider clientId="604189500317-l0pqk1h5d9bhnoiutpi5gcgn6s18au73.apps.googleusercontent.com">
      <App />
  </GoogleOAuthProvider>,
  document.getElementById('root')
);